interface SendDataParams {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  endpoint: string;
  body?: Record<string, unknown>;
  headers?: HeadersInit | null;
  signal?: AbortSignal | null;
  loadingElements?: HTMLElement[];
  onSuccess?: (response: unknown) => void;
  onError?: (error: { message: string }) => void;
}

/**
 * W8shipping API Client
 */
export class W8ShippingAPIClient {
  private baseUrl: string;
  private nonce: string;
  private locale: string;

  constructor(baseUrl: string, nonce: string, locale: string) {
    this.baseUrl = baseUrl;
    this.nonce = nonce;
    this.locale = locale;
  }

  private async request({
    method,
    endpoint,
    body = {},
    headers = {},
    signal = null,
    loadingElements = [],
    onSuccess = () => {},
    onError = () => {},
  }: SendDataParams): Promise<void> {
    // Manage loading state
    toggleLoading(loadingElements, true);
    const turnstileInput = document.querySelector(
      'input[name="cf-turnstile-response"]',
    ) as HTMLInputElement | null;

    const headersDefault = {
      'Content-Type': 'application/json',
      'X-WP-Nonce': this.nonce,
      'Accept-Language': this.locale,
    };

    if (turnstileInput) {
      headersDefault['CF-Turnstile'] = turnstileInput.value;
      headersDefault['CF-Turnstile-Type'] = 'invisible';
    }

    headers = {
      ...headersDefault,
      ...headers,
    };

    try {
      const response = await fetch(`${this.baseUrl}/${endpoint}`, {
        method,
        signal,
        headers: new Headers(headers),
        body: method === 'GET' ? null : JSON.stringify(body),
      });

      const responseData = await response.json();

      if (response.ok) {
        onSuccess(responseData);
      } else {
        console.error(
          'API Error at endpoint:',
          endpoint,
          'Response:',
          responseData,
        );
        onError(responseData);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Fetch Error at endpoint:', endpoint, 'Error:', error);
        onError({ message: error.message });
      }
    } finally {
      toggleLoading(loadingElements, false);

      const event = new CustomEvent('W8ShippingRequestFinished');
      document.dispatchEvent(event);
    }
  }

  public get(
    endpoint: string,
    body: Record<string, unknown>,
    params: Omit<SendDataParams, 'method' | 'body'>,
  ): Promise<void> {
    const queryString = body
      ? '?' + new URLSearchParams(body as Record<string, string>).toString()
      : '';

    return this.request({
      ...params,
      method: 'GET',
      endpoint: `${endpoint}${queryString}`,
    });
  }

  public post(
    endpoint: string,
    params: Omit<SendDataParams, 'method'>,
  ): Promise<void> {
    return this.request({ ...params, method: 'POST', endpoint });
  }

  public patch(
    endpoint: string,
    params: Omit<SendDataParams, 'method'>,
  ): Promise<void> {
    return this.request({ ...params, method: 'PATCH', endpoint });
  }

  public put(
    endpoint: string,
    params: Omit<SendDataParams, 'method'>,
  ): Promise<void> {
    return this.request({ ...params, method: 'PUT', endpoint });
  }

  public delete(
    endpoint: string,
    params: Omit<SendDataParams, 'method'>,
  ): Promise<void> {
    return this.request({ ...params, method: 'DELETE', endpoint });
  }
}

/**
 * Toggles a loading class on elements.
 */
function toggleLoading(elements: HTMLElement[] = [], state: boolean): void {
  elements.forEach((el) => el.classList.toggle('loading', state));
}
